/**
 * Centralized metric descriptions for Analytics tooltips.
 * Keep descriptions short — one or two sentences max.
 */

// ---------------------------------------------------------------------------
// Today View
// ---------------------------------------------------------------------------

export const TODAY_NEEDS_ATTENTION =
    'Items requiring immediate action — overdue documents, pending fixes, unpaid contracts, and stale offers.';

export const TODAY_OVERDUE_DOCUMENTS =
    'Documents in MISSING, FIX REQUIRED, or WAITING FOR APPROVAL status for 14+ days. Calculated as: now − document last status update > 14 days.';

export const TODAY_DOCUMENTS_NEEDING_FIXES =
    'Documents flagged as FIX REQUIRED. The client needs to resubmit before the case can proceed.';

export const TODAY_UNPAID_CONTRACTS =
    'Cases where a signed agreement exists but the client has not yet paid the milestone fees — money is owed but not received. Calculated from milestones with payment status PENDING or PARTIALLY PAID on applications that have an offer sent date.';

export const TODAY_UNCONVERTED_OFFERS =
    "Cases where an offer was sent but no agreement has been signed yet — the client hasn't committed and the deal is still open. Calculated from applications with status OFFER and payment status NOT PAID, that have an offer sent date but no signed agreement.";

export const TODAY_REVENUE_AT_RISK =
    'Combined value of Unpaid Contracts (signed, not paid) + Unconverted Offers (sent, not signed). Calculated as: unpaidContractsTotal + unconvertedOffersTotal. Click a card to see details.';

export const TODAY_TEAM_HEALTH =
    "Each member's overdue rate. Calculated as: (overdue cases / active cases) × 100. Green < 25%, Amber 25–49%, Red ≥ 50%. Overdue = no status update for 30+ days.";

export const TODAY_ROLE_HEALTH =
    'Aggregated health per team — active, overdue, and on-track cases across all members of the team.';

// ---------------------------------------------------------------------------
// Conversion View
// ---------------------------------------------------------------------------

export const CONVERSION_FUNNEL =
    'Lead-to-paid conversion funnel. Each stage shows count, conversion rate, and drop-off. Toggle dimensions for breakdowns by program, country, or practitioner.';

export const CONVERSION_SPEED =
    'Average days between each funnel stage. Compared against the previous period of equal length.';

export const CONVERSION_REGISTERED =
    'Total applications created in the selected period. All applications matching date and scope filters.';

export const CONVERSION_ASSESSMENT_COMPLETED =
    'Applications that moved past the assessment stage. Calculated as: applications whose status is no longer ASSESSMENT or NOT ASSIGNED.';

export const CONVERSION_OFFER_SENT =
    'Applications where an offer has been sent to the client. Calculated as: applications with a non-null offer sent date.';

export const CONVERSION_PAID =
    'Applications that are fully paid. Calculated as: applications with payment status = FULLY PAID.';

export const CONVERSION_RATE_OVERALL =
    'End-to-end conversion from lead to paid client. Calculated as: (paid / registered) × 100.';

export const CONVERSION_SPEED_REGISTRATION_TO_ASSESSMENT =
    'Average days from registration to completing assessment. Calculated as: (last status update − created date) in days, capped at 7 days.';

export const CONVERSION_SPEED_ASSESSMENT_TO_OFFER =
    'Average days from application creation to offer sent. Calculated as: (offer sent date − created date) in days.';

export const CONVERSION_SPEED_OFFER_TO_PAID =
    'Average days from offer sent to full payment. Calculated as: (fully paid date − offer sent date) in days.';

// ---------------------------------------------------------------------------
// Revenue View
// ---------------------------------------------------------------------------

export const REVENUE_BY_PROGRAM =
    "Revenue from fully paid cases, grouped by program. Shows case count, average fee per case, and each program's share of total revenue. Calculated as: sum of milestone prices for FULLY PAID applications per program.";

export const REVENUE_RECOVERY =
    "Revenue at risk in two buckets. Unconverted Offers: offer sent but no agreement signed — client hasn't committed. Unpaid Contracts: agreement signed but payment outstanding — money owed but not received. Click a bucket to drill down.";

export const REVENUE_UNCONVERTED_OFFERS =
    'Cases where an offer was sent but no agreement has been signed — the deal is still open. Aged by days since offer sent into buckets: 0–7 (green), 8–14 (amber), 15–30 (red), 31+ (dark red).';

export const REVENUE_UNPAID_CONTRACTS =
    'Cases where a signed agreement exists but payment is still outstanding — money is owed but not received. Aged by days since milestone was created into buckets: 0–7 (green), 8–14 (amber), 15–30 (red), 31+ (dark red).';

export const REVENUE_PAYMENT_VELOCITY =
    'Average days from offer sent to full payment, tracked monthly. Calculated as: (fully paid date − offer sent date) in days, grouped by month of payment. Down = faster collection.';

export const REVENUE_PAYMENT_VELOCITY_CALCULATION =
    'For each paid case: (fully paid date − offer sent date) in days. Averaged per month using the month the payment was received.';

export const REVENUE_SERVICE_PRICE_HISTOGRAM =
    'Distribution of case prices across selected services — shows average, median, min, and max. Cases are grouped into equal-width price buckets.';

// ---------------------------------------------------------------------------
// Operations View
// ---------------------------------------------------------------------------

export const OPERATIONS_CLIENT_STATUS =
    'How many applications entered each file status during the selected period. Counted from status-change events; an application that moved through multiple statuses is counted once under each status it entered. Total is the sum of all status counts and may exceed distinct applications.';

export const OPERATIONS_DOCUMENT_HEALTH =
    'Overdue tab: documents stuck in MISSING or WAITING FOR APPROVAL status. Fixes tab: documents in FIX REQUIRED status, with patterns showing which document types get rejected most. Grouped by client.';

export const OPERATIONS_CASE_PROCESSING_SPEED_OWNER =
    'How long each practitioner takes to process a case, from offer sent to completion. Calculated as: for each completed case (SUCCESS or FAIL), compute (last status update date − offer sent date) in days, then average across all completed cases for that practitioner. Cases still in progress are excluded.';

export const OPERATIONS_CASE_PROCESSING_SPEED_PERSONAL =
    'Your average case processing time compared to the agency-wide average. Calculated the same way — (last status update − offer sent) in days, averaged. Lower = faster case completion.';

export const OPERATIONS_CASE_PROCESSING_SPEED_CALCULATION =
    'For each completed case (SUCCESS or FAIL): take the number of days between offer sent and the last status update. Then average all those values. Practitioner average = sum of their days / their completed cases. Team average = sum of all days / all completed cases. Only cases with a valid offer sent date and a terminal status are included.';

export const OPERATIONS_TEAM_AVG_SPEED =
    'Agency-wide average processing speed. Calculated as: sum of all processing days across every practitioner / total completed cases.';

export const OPERATIONS_DOCUMENTS_UPLOADED =
    'Documents uploaded per team member or client in the selected period. Choose a team to see each person.';

export const OPERATIONS_CASE_STATUS_TRACKING =
    'Document status changes made by agents in the selected period. Shows total status moves, documents moved to Fix Required, and documents moved to Pre-Approved. Click a person for the full change list.';

export const OPERATIONS_AGENT_DOCUMENT_STATUSES =
    'Agent matrix of document status changes in the selected period. Each status column is a count of times documents were moved TO that status (not from). Click an agent to see the document list and destination status.';

export const OPERATIONS_MESSAGES_POSTED =
    'Messages posted per team member or client in the selected period. Choose a team to see each person.';

export const OPERATIONS_TEAM_LEVEL =
    'Documents uploaded, messages posted, and case speed for each team in the selected period. Client totals match the Clients lists below.';

export const OPERATIONS_TEAM_MEMBERS =
    'Team members who own a case created in the selected period.';

export const OPERATIONS_TEAM_AVG_PROCESSING =
    'Average days from offer sent to the last status update, across this team’s finished cases. A lower number means faster completion. Not shown for clients.';

export const OPERATIONS_TEAM_COMPLETED =
    'Cases created in the selected period that this team finished, with an offer sent. Not shown for clients.';

export const OPERATIONS_TEAM_ACTIVE =
    'Cases created in the selected period that this team still has in progress. Not shown for clients.';

export const OPERATIONS_TEAM_DOCUMENTS_UPLOADED =
    'Documents uploaded or replaced in the selected period by this team, or by clients. This total matches the list below.';

export const OPERATIONS_TEAM_MESSAGES_POSTED =
    'Messages posted in the selected period by this team, or by clients. This total matches the list below.';

// ---------------------------------------------------------------------------
// Provider Comparison View (Summary)
// ---------------------------------------------------------------------------

export const PROVIDER_COMPARISON =
    'Monthly metrics across service providers — assessments completed, applications fully paid, and applications submitted. Toggle providers in the legend.';

export const PROVIDER_COMPLETED_ASSESSMENTS =
    'Assessments completed per month, per provider. Calculated from users whose assessment completion date falls within the month.';

export const PROVIDER_FULLY_PAID_APPLICATIONS =
    'Applications fully paid per month, per provider. Calculated from applications whose fully paid date falls within the month.';

export const PROVIDER_SUBMITTED_APPLICATIONS =
    'Applications submitted per month, per provider. Calculated from status change events to SUBMITTED, deduplicated per application per month.';

export const PROVIDER_MONTHLY_AVERAGE =
    'Monthly average across all providers for the selected metric.';

// ---------------------------------------------------------------------------
// Unpaid vs Unconverted
// ---------------------------------------------------------------------------

export const UNPAID_VS_UNCONVERTED =
    "Two distinct types of revenue at risk. Unpaid Contracts: a signed agreement exists but the client has not paid the milestone fees — money is owed but not received. Unconverted Offers: an offer was sent but no agreement has been signed — the client hasn't committed and the deal is still open.";

// ---------------------------------------------------------------------------
// Common / Shared
// ---------------------------------------------------------------------------

export const OVERDUE_CASES =
    'Active cases with no status update for 30+ days. Calculated as: now − last status update > 30 days, filtered to non-closed statuses.';

export const OVERDUE_RATE =
    'Percentage of active cases that are overdue. Calculated as: (overdue cases / active cases) × 100.';

export const HEALTH_STATUS =
    'Traffic-light indicator based on overdue rate. Green = overdue rate < 25%, Amber = 25–49%, Red = ≥ 50%.';

export const AGING_BUCKETS =
    'Time-based severity buckets: 0–7 days (green), 8–14 days (amber), 15–30 days (red), 31+ days (dark red). Used by revenue, unpaid contracts, and unconverted offers.';

export const PIPELINE_VALUE =
    'Total monetary value of in-progress unpaid applications. Calculated as: sum of milestone prices where payment status is NOT PAID and case is not in a closed status.';

export const CONVERSION_RATE =
    'Percentage of items progressing to the next funnel stage. Calculated as: (next stage count / current stage count) × 100.';

export const ABANDONMENT_RATE =
    'Percentage of applications that ended up in closed or rejected statuses. Calculated as: (rejected count / total applications) × 100.';

export const SLA_BREACH =
    'Case or document that has exceeded the SLA time window. Thresholds: 30 days for cases (no status update), 14 days for documents (no status update).';
